import type OccupancyStatus from 'app/frontend/types/custom/OccupancyStatus'

type MapConfig = {
  ResX: number
  ResY: number
  Zoom: number
  MaxZoom: number
  MinX: number
  MinY: number
}

export type MapContent = {
  Name: string
  BoundingBox: {
    Item1: { X: number; Y: number }
    Item2: { X: number; Y: number }
  }
  ImageURL: string
  PortalURL: string
  ThumbnailURL: string
  VideoURL: string
  POIs: MapPOI[]
}

export type MapPOI = {
  Name: string
  Type: string
  X: number
  Y: number
  VideoTimestamp?: number
}

type MapData = {
  MD5: string
  Config: MapConfig
  Content: MapContent
}

export type OccupancyData = {
  main_map_id: number
  occupants: {
    id: number
    map_id: number
    plot_id: number
    shard_index: number
    status: OccupancyStatus
    ranked_choice: number
    user_id: number | null
    character_id: number | null
    note?: string
    updated_at: string
  }[]
  users: {
    id: number
    name: string
    main_character_id: number | null
    character_ids: number[]
    claimed_plot_on_maps: number[]
    approved_plot_on_maps: number[]
  }[]
}

export type OccupantsByPlot = {
  [key: string]: Occupant[]
}

export type Occupant = {
  id: number
  plot_id: number
  shard_index: number
  character: CharacterInfo
  status: OccupancyStatus
  ranked_choice: number
  note?: string
  updated_at: string
}

export type CharacterInfo = {
  key: string
  name: string
  characterClass?: string
  userId?: number
  userHasApprovedPlot?: boolean
  userHasClaimedPlot?: boolean
  inTeam: boolean
  guild_rank_name?: string | null
  guild_public_note?: string | null
  guild_officer_note?: string | null
  guild_rank?: number
}

export type MapContextType = {
  mapId: number
  shardIndex: number
  setShardIndex: (index: number) => void
  userHasApprovedPlot: boolean
  userHasClaimedPlot: boolean
  mapConfig: MapConfig
  neighborhood: MapContent
  occupantsByPlot: OccupantsByPlot
  relevantCharacters: CharacterInfo[]
  characterForUser?: CharacterInfo
  occupantsForUser: Occupant[]
  user?: wowaudit.User
  guild: wowaudit.Guild
  guildMembers: wowaudit.CharacterReference[]
  onPlotClick: (plot: MapPOI | null) => void
  mutateOccupancyData?: (
    data?: OccupancyData,
    shouldRevalidate?: boolean,
  ) => Promise<OccupancyData | undefined>
}

export type HoverContextType = {
  hoveringPlot: string | null
  setHoveringPlot: (plot: string | null) => void
}

export const mapIdToFaction = {
  '1': 'alliance',
  '2': 'horde',
}

const MAPS: { [key: number]: MapData } = {
  1: {
    MD5: '0d0a209218b435d9881a97dcac9e33cb',
    Config: {
      ResX: 7680,
      ResY: 6144,
      Zoom: 1,
      MaxZoom: 5,
      MinX: 24,
      MinY: 20,
    },
    Content: {
      Name: 'Alliance',
      ImageURL: 'https://data.wowaudit.com/img/housing/neighborhood-1.jpg',
      ThumbnailURL:
        'https://data.wowaudit.com/img/housing/neighborhood-1_thumbnail.png',
      PortalURL:
        'https://data.wowaudit.com/img/housing/icon_portal_alliance.png',
      VideoURL: 'https://www.youtube.com/watch?v=Gjqd6LsGWl8',
      BoundingBox: {
        Item1: {
          X: 1168.75,
          Y: -2454.17,
        },
        Item2: {
          X: 4762.5,
          Y: 2935.42,
        },
      },
      POIs: [
        {
          Name: '28',
          Type: 'Plot',
          X: 3642.9167,
          Y: 1395.1598,
          VideoTimestamp: 1068,
        },
        {
          Name: '40',
          Type: 'Plot',
          X: 3554.9297,
          Y: 1468.2604,
          VideoTimestamp: 1003,
        },
        {
          Name: '25',
          Type: 'Plot',
          X: 3443.7605,
          Y: 1502.4305,
          VideoTimestamp: 980,
        },
        {
          Name: '12',
          Type: 'Plot',
          X: 3318.9297,
          Y: 1548.4305,
          VideoTimestamp: 938,
        },
        {
          Name: '4',
          Type: 'Plot',
          X: 3497.217,
          Y: 1365.7709,
          VideoTimestamp: 1034,
        },
        {
          Name: '24',
          Type: 'Plot',
          X: 3835.9836,
          Y: 1166.7552,
          VideoTimestamp: 770,
        },
        {
          Name: '43',
          Type: 'Plot',
          X: 3670.5027,
          Y: 1182.9548,
          VideoTimestamp: 845,
        },
        {
          Name: '29',
          Type: 'Plot',
          X: 3529.8264,
          Y: 1187.6459,
          VideoTimestamp: 722,
        },
        {
          Name: '19',
          Type: 'Plot',
          X: 3334.8733,
          Y: 1133.4913,
          VideoTimestamp: 654,
        },
        {
          Name: '50',
          Type: 'Plot',
          X: 3268.7395,
          Y: 1029.9497,
          VideoTimestamp: 604,
        },
        {
          Name: '52',
          Type: 'Plot',
          X: 3391.7795,
          Y: 970.18054,
          VideoTimestamp: 559,
        },
        {
          Name: '34',
          Type: 'Plot',
          X: 3450.2605,
          Y: 771.21704,
          VideoTimestamp: 4045,
        },
        {
          Name: '39',
          Type: 'Plot',
          X: 3570.98,
          Y: 677.2847,
          VideoTimestamp: 838,
        },
        {
          Name: '45',
          Type: 'Plot',
          X: 3425.5503,
          Y: 655.30554,
          VideoTimestamp: 400,
        },
        {
          Name: '42',
          Type: 'Plot',
          X: 3579.25,
          Y: 470.32465,
          VideoTimestamp: 247,
        },
        {
          Name: '44',
          Type: 'Plot',
          X: 3686.4722,
          Y: 369.92014,
          VideoTimestamp: 156,
        },
        {
          Name: '9',
          Type: 'Plot',
          X: 3524.3003,
          Y: 342.8611,
          VideoTimestamp: 207,
        },
        {
          Name: '27',
          Type: 'Plot',
          X: 3672.77,
          Y: 244.07118,
          VideoTimestamp: 80,
        },
        {
          Name: '7',
          Type: 'Plot',
          X: 3576.4497,
          Y: 234.59549,
          VideoTimestamp: 112,
        },
        {
          Name: '51',
          Type: 'Plot',
          X: 3337.9705,
          Y: 529.1059,
          VideoTimestamp: 1166,
        },
        {
          Name: '47',
          Type: 'Plot',
          X: 3334.5815,
          Y: 391.18576,
          VideoTimestamp: 1210,
        },
        {
          Name: '46',
          Type: 'Plot',
          X: 3198.89,
          Y: 365.13022,
          VideoTimestamp: 1253,
        },
        {
          Name: '49',
          Type: 'Plot',
          X: 3088.7605,
          Y: 406.67014,
          VideoTimestamp: 1334,
        },
        {
          Name: '54',
          Type: 'Plot',
          X: 3157.9001,
          Y: 261.61285,
          VideoTimestamp: 1289,
        },
        {
          Name: '16',
          Type: 'Plot',
          X: 2997.7205,
          Y: 84.270836,
          VideoTimestamp: 3152,
        },
        {
          Name: '11',
          Type: 'Plot',
          X: 2913.6128,
          Y: 94.43577,
          VideoTimestamp: 3111,
        },
        {
          Name: '48',
          Type: 'Plot',
          X: 3032.9705,
          Y: 858.59204,
          VideoTimestamp: 1432,
        },
        {
          Name: '53',
          Type: 'Plot',
          X: 2972.6597,
          Y: 792.783,
          VideoTimestamp: 1464,
        },
        {
          Name: '13',
          Type: 'Plot',
          X: 2842.2449,
          Y: 908.9774,
          VideoTimestamp: 1510,
        },
        {
          Name: '14',
          Type: 'Plot',
          X: 2762.5703,
          Y: 1066.3698,
          VideoTimestamp: 1568,
        },
        {
          Name: '36',
          Type: 'Plot',
          X: 2759.7708,
          Y: 1167.5903,
          VideoTimestamp: 1609,
        },
        {
          Name: '5',
          Type: 'Plot',
          X: 2636.1875,
          Y: 996.3559,
          VideoTimestamp: 1653,
        },
        {
          Name: '35',
          Type: 'Plot',
          X: 2474.4705,
          Y: 955.80383,
          VideoTimestamp: 1744,
        },
        {
          Name: '30',
          Type: 'Plot',
          X: 2662.3604,
          Y: 831.4653,
          VideoTimestamp: 1909,
        },
        {
          Name: '1',
          Type: 'Plot',
          X: 2710.0815,
          Y: 642.7639,
          VideoTimestamp: 2006,
        },
        {
          Name: '26',
          Type: 'Plot',
          X: 2595.7656,
          Y: 604.1858,
          VideoTimestamp: 2040,
        },
        {
          Name: '37',
          Type: 'Plot',
          X: 2485.5105,
          Y: 522.3281,
          VideoTimestamp: 2110,
        },
        {
          Name: '10',
          Type: 'Plot',
          X: 2427.8108,
          Y: 305.5816,
          VideoTimestamp: 2229,
        },
        {
          Name: '2',
          Type: 'Plot',
          X: 2553.0295,
          Y: 169.29861,
          VideoTimestamp: 2265,
        },
        {
          Name: '15',
          Type: 'Plot',
          X: 2766.0703,
          Y: 164.05208,
          VideoTimestamp: 3051,
        },
        {
          Name: '8',
          Type: 'Plot',
          X: 2333.5798,
          Y: 29.23611,
          VideoTimestamp: 2462,
        },
        {
          Name: '33',
          Type: 'Plot',
          X: 2510.842,
          Y: -70.93056,
          VideoTimestamp: 2420,
        },
        {
          Name: '18',
          Type: 'Plot',
          X: 2249.9307,
          Y: -218.65625,
          VideoTimestamp: 2631,
        },
        {
          Name: '41',
          Type: 'Plot',
          X: 2159.6077,
          Y: -355.83508,
          VideoTimestamp: 2695,
        },
        {
          Name: '20',
          Type: 'Plot',
          X: 2431.8994,
          Y: -301.89758,
          VideoTimestamp: 2577,
        },
        {
          Name: '38',
          Type: 'Plot',
          X: 2568.0295,
          Y: -299.7604,
          VideoTimestamp: 2793,
        },
        {
          Name: '6',
          Type: 'Plot',
          X: 2700.4602,
          Y: -427.24307,
          VideoTimestamp: 2870,
        },
        {
          Name: '21',
          Type: 'Plot',
          X: 2622.3098,
          Y: -637.9757,
          VideoTimestamp: 2923,
        },
        {
          Name: '0',
          Type: 'Plot',
          X: 2987.019,
          Y: -224.60591,
          VideoTimestamp: 3196,
        },
        {
          Name: '17',
          Type: 'Plot',
          X: 2971.8699,
          Y: -350.03125,
          VideoTimestamp: 3302,
        },
        {
          Name: '22',
          Type: 'Plot',
          X: 3039.1902,
          Y: -561.6233,
          VideoTimestamp: 3423,
        },
        {
          Name: '3',
          Type: 'Plot',
          X: 3189.0095,
          Y: -488.9809,
          VideoTimestamp: 3384,
        },
        {
          Name: '32',
          Type: 'Plot',
          X: 3171.8699,
          Y: -646.6233,
          VideoTimestamp: 3453,
        },
        {
          Name: '23',
          Type: 'Plot',
          X: 3339.0105,
          Y: -607.7292,
          VideoTimestamp: 3506,
        },
        {
          Name: '31',
          Type: 'Plot',
          X: 3329.9497,
          Y: -393.56076,
          VideoTimestamp: 3552,
        },
        {
          Name: "Entrance Portal, Founder's Point",
          Type: 'TaxiNode',
          X: 3770.83,
          Y: -119.46,
        },
        {
          Name: "Town Center, Founder's Point",
          Type: 'TaxiNode',
          X: 3344.19,
          Y: 169.878,
        },
        {
          Name: "Mear's Farm, Founder's Point",
          Type: 'TaxiNode',
          X: 3534.64,
          Y: 1068.83,
        },
        {
          Name: "The Outer Banks, Founder's Point",
          Type: 'TaxiNode',
          X: 3115.5,
          Y: 1191.86,
        },
        {
          Name: "Small Aerie, Founder's Point",
          Type: 'TaxiNode',
          X: 3146.37,
          Y: 598.96,
        },
        {
          Name: "Gilded Oaks, Founder's Point",
          Type: 'TaxiNode',
          X: 2508.28,
          Y: 881.25,
        },
        {
          Name: "Brumewood Hollow, Founder's Point",
          Type: 'TaxiNode',
          X: 2679.02,
          Y: -101.649,
        },
        {
          Name: "Stoneveil Ridge, Founder's Point",
          Type: 'TaxiNode',
          X: 3276.4,
          Y: -499.554,
        },
        {
          Name: 'Stormwind Portal',
          Type: 'Portal',
          X: 3806,
          Y: -160,
        },
        {
          Name: 'Town Vendors (Alliance Decor)',
          Type: 'Vendor',
          X: 3382,
          Y: 133,
        },
        {
          Name: 'Trevor Grenner <Decor Vendor>',
          Type: 'Vendor',
          X: 3277,
          Y: 40,
        },
        {
          Name: 'Pirate Vendors (Horde Decor)',
          Type: 'Vendor',
          X: 1890,
          Y: -434,
        },
      ],
    },
  },
  2: {
    MD5: '0d0a209218b435d9881a97dcac9e33cb',
    Config: {
      ResX: 6656,
      ResY: 7168,
      Zoom: 1,
      MaxZoom: 5,
      MinX: 24,
      MinY: 21,
    },
    Content: {
      Name: 'Horde',
      ImageURL: 'https://data.wowaudit.com/img/housing/neighborhood-2.jpg',
      ThumbnailURL:
        'https://data.wowaudit.com/img/housing/neighborhood-2_thumbnail.png',
      PortalURL: 'https://data.wowaudit.com/img/housing/icon_portal_horde.png',
      VideoURL: 'https://www.youtube.com/watch?v=lKFavzr7iGk',
      BoundingBox: {
        Item1: {
          X: -212.5,
          Y: -2956.25,
        },
        Item2: {
          X: 4339.583,
          Y: 3870.833,
        },
      },
      POIs: [
        {
          Name: '2',
          Type: 'Plot',
          X: 1922.6545,
          Y: 982.63544,
          VideoTimestamp: 1632,
        },
        {
          Name: '25',
          Type: 'Plot',
          X: 1875.2396,
          Y: 678.3299,
          VideoTimestamp: 1507,
        },
        {
          Name: '36',
          Type: 'Plot',
          X: 1768.2031,
          Y: 631.8768,
          VideoTimestamp: 1459,
        },
        {
          Name: '9',
          Type: 'Plot',
          X: 1666.4497,
          Y: 1084.4132,
          VideoTimestamp: 1807,
        },
        {
          Name: '18',
          Type: 'Plot',
          X: 1565.7344,
          Y: 1003.0903,
          VideoTimestamp: 1840,
        },
        {
          Name: '28',
          Type: 'Plot',
          X: 1676.151,
          Y: 950.6059,
          VideoTimestamp: 1728,
        },
        {
          Name: '41',
          Type: 'Plot',
          X: 1349.1685,
          Y: 581.26044,
          VideoTimestamp: 2437,
        },
        {
          Name: '27',
          Type: 'Plot',
          X: 1186.882,
          Y: 493.1875,
          VideoTimestamp: 2529,
        },
        {
          Name: '30',
          Type: 'Plot',
          X: 934.11456,
          Y: 505.2257,
          VideoTimestamp: 2596,
        },
        {
          Name: '0',
          Type: 'Plot',
          X: 1284.8108,
          Y: 1113.6892,
          VideoTimestamp: 2008,
        },
        {
          Name: '50',
          Type: 'Plot',
          X: 1032.8976,
          Y: 660.48267,
          VideoTimestamp: 2341,
        },
        {
          Name: '33',
          Type: 'Plot',
          X: 1153.1389,
          Y: 794.9965,
          VideoTimestamp: 2299,
        },
        {
          Name: '11',
          Type: 'Plot',
          X: 1064.6423,
          Y: 867.97394,
          VideoTimestamp: 2258,
        },
        {
          Name: '26',
          Type: 'Plot',
          X: 1031.0555,
          Y: 1021.9861,
          VideoTimestamp: 2064,
        },
        {
          Name: '17',
          Type: 'Plot',
          X: 912.0278,
          Y: 853.3281,
          VideoTimestamp: 2220,
        },
        {
          Name: '54',
          Type: 'Plot',
          X: 707.7066,
          Y: 826.8542,
          VideoTimestamp: 2796,
        },
        {
          Name: '43',
          Type: 'Plot',
          X: 606.92883,
          Y: 726.75,
          VideoTimestamp: 2672,
        },
        {
          Name: '12',
          Type: 'Plot',
          X: 505.46008,
          Y: 643.3785,
          VideoTimestamp: 2735,
        },
        {
          Name: '24',
          Type: 'Plot',
          X: 189.7448,
          Y: 666.4358,
          VideoTimestamp: 2956,
        },
        {
          Name: '32',
          Type: 'Plot',
          X: 660.8455,
          Y: 393.08508,
          VideoTimestamp: 3019,
        },
        {
          Name: '22',
          Type: 'Plot',
          X: 515.11633,
          Y: 287.18402,
          VideoTimestamp: 2886,
        },
        {
          Name: '49',
          Type: 'Plot',
          X: 401.3455,
          Y: 159.68056,
          VideoTimestamp: 3069,
        },
        {
          Name: '37',
          Type: 'Plot',
          X: 266.0104,
          Y: 101.85938,
          VideoTimestamp: 3236,
        },
        {
          Name: '19',
          Type: 'Plot',
          X: 350.31076,
          Y: -83.204865,
          VideoTimestamp: 3280,
        },
        {
          Name: '6',
          Type: 'Plot',
          X: 619.86285,
          Y: 13.432292,
          VideoTimestamp: 3380,
        },
        {
          Name: '51',
          Type: 'Plot',
          X: 599.3403,
          Y: -132.3368,
          VideoTimestamp: 3335,
        },
        {
          Name: '7',
          Type: 'Plot',
          X: 560.75696,
          Y: -280.7361,
          VideoTimestamp: 3425,
        },
        {
          Name: '3',
          Type: 'Plot',
          X: 763.69794,
          Y: -235.79167,
          VideoTimestamp: 3460,
        },
        {
          Name: '5',
          Type: 'Plot',
          X: 718.9219,
          Y: -396.22397,
          VideoTimestamp: 3567,
        },
        {
          Name: '1',
          Type: 'Plot',
          X: 762.7483,
          Y: -516.19617,
          VideoTimestamp: 3636,
        },
        {
          Name: '13',
          Type: 'Plot',
          X: 886.38367,
          Y: -577.75696,
          VideoTimestamp: 3705,
        },
        {
          Name: '14',
          Type: 'Plot',
          X: 1190.8473,
          Y: -678.27606,
          VideoTimestamp: 3847,
        },
        {
          Name: '8',
          Type: 'Plot',
          X: 1342.0399,
          Y: -679.67706,
          VideoTimestamp: 3902,
        },
        {
          Name: '10',
          Type: 'Plot',
          X: 1239.9028,
          Y: -192.89062,
          VideoTimestamp: 3964,
        },
        {
          Name: '40',
          Type: 'Plot',
          X: 983.2465,
          Y: 165.41493,
          VideoTimestamp: 4205,
        },
        {
          Name: '29',
          Type: 'Plot',
          X: 1133.1267,
          Y: 136.78125,
          VideoTimestamp: 4159,
        },
        {
          Name: '48',
          Type: 'Plot',
          X: 1225.8004,
          Y: 21.975695,
          VideoTimestamp: 4119,
        },
        {
          Name: '34',
          Type: 'Plot',
          X: 1337.4584,
          Y: -77.62847,
          VideoTimestamp: 4045,
        },
        {
          Name: '47',
          Type: 'Plot',
          X: 1392.9757,
          Y: 113.53125,
          VideoTimestamp: 4257,
        },
        {
          Name: '16',
          Type: 'Plot',
          X: 1511.1216,
          Y: -36.552082,
          VideoTimestamp: 261,
        },
        {
          Name: '4',
          Type: 'Plot',
          X: 1570.4479,
          Y: -257.29514,
          VideoTimestamp: 397,
        },
        {
          Name: '21',
          Type: 'Plot',
          X: 1691.3473,
          Y: -203.3698,
          VideoTimestamp: 336,
        },
        {
          Name: '45',
          Type: 'Plot',
          X: 1877.9531,
          Y: -299.80902,
          VideoTimestamp: 1182,
        },
        {
          Name: '44',
          Type: 'Plot',
          X: 1936.0295,
          Y: -451.77258,
          VideoTimestamp: 1249,
        },
        {
          Name: '20',
          Type: 'Plot',
          X: 1833.1007,
          Y: -504.59027,
          VideoTimestamp: 1289,
        },
        {
          Name: '31',
          Type: 'Plot',
          X: 2111.1267,
          Y: -295.39932,
          VideoTimestamp: 1127,
        },
        {
          Name: '46',
          Type: 'Plot',
          X: 2289.8682,
          Y: -560.36804,
          VideoTimestamp: 939,
        },
        {
          Name: '39',
          Type: 'Plot',
          X: 2253.4062,
          Y: -903.99304,
          VideoTimestamp: 838,
        },
        {
          Name: '15',
          Type: 'Plot',
          X: 2107.5261,
          Y: -745.5347,
          VideoTimestamp: 797,
        },
        {
          Name: '23',
          Type: 'Plot',
          X: 1960.0903,
          Y: -706.04865,
          VideoTimestamp: 756,
        },
        {
          Name: '38',
          Type: 'Plot',
          X: 1891.6459,
          Y: -883.6302,
          VideoTimestamp: 700,
        },
        {
          Name: '35',
          Type: 'Plot',
          X: 1789.6545,
          Y: -993.4948,
          VideoTimestamp: 662,
        },
        {
          Name: '42',
          Type: 'Plot',
          X: 1725.1198,
          Y: -872.6024,
          VideoTimestamp: 624,
        },
        {
          Name: '52',
          Type: 'Plot',
          X: 1631.3055,
          Y: -691.6649,
          VideoTimestamp: 542,
        },
        {
          Name: '53',
          Type: 'Plot',
          X: 1602.7448,
          Y: -569.16144,
          VideoTimestamp: 480,
        },
        {
          Name: 'Entrance Gate, Razorwind Shores',
          Type: 'TaxiNode',
          X: 2011.73,
          Y: 128.773,
        },
        {
          Name: 'Saltfang Shoals East, Razorwind Shores',
          Type: 'TaxiNode',
          X: 902.149,
          Y: -775.415,
        },
        {
          Name: "Runetotem's Bounty North, Razorwind Shores",
          Type: 'TaxiNode',
          X: 1800.15,
          Y: 958.467,
        },
        {
          Name: "Runetotem's Bounty South, Razorwind Shores",
          Type: 'TaxiNode',
          X: 1154.71,
          Y: 613.434,
        },
        {
          Name: 'The Bluffs, Razorwind Shores',
          Type: 'TaxiNode',
          X: 616.262,
          Y: 274.839,
        },
        {
          Name: 'Cragthorn Highlands, Razorwind Shores',
          Type: 'TaxiNode',
          X: 1769.78,
          Y: -651.521,
        },
        {
          Name: 'The Common, Razorwind Shores',
          Type: 'TaxiNode',
          X: 1624.42,
          Y: 206.559,
        },
        {
          Name: 'The Bloom, Razorwind Shores',
          Type: 'TaxiNode',
          X: 1067.29,
          Y: -169.34,
        },
        {
          Name: 'Orgrimmar Portal',
          Type: 'Portal',
          X: 2091,
          Y: 190,
        },
        {
          Name: 'Town Vendors (Horde Decor)',
          Type: 'Vendor',
          X: 1716,
          Y: 196,
        },
        {
          Name: 'Pirate Vendors (Alliance Decor)',
          Type: 'Vendor',
          X: 1013,
          Y: 1119,
        },
      ],
    },
  },
}

export function loadNeighborhoodMapData(mapId: number) {
  return MAPS[mapId]
}

export function WoWCoordsToPixel(
  mapId: number,
  x: number,
  y: number,
): { x: number; y: number } {
  const maxSize = 51200 / 3
  const mapSize = maxSize * 2
  const adtSize = mapSize / 64
  const pxPerCoord = adtSize / 512

  const config = MAPS[mapId].Config
  const offsetX = (config.MinX * adtSize) / pxPerCoord
  const offsetY = (config.MinY * adtSize) / pxPerCoord

  const tempx = (mapSize / 2 + y * -1) / pxPerCoord - offsetX
  const tempy = (mapSize / 2 + x * -1) / pxPerCoord - offsetY

  return { x: tempx, y: tempy }
}

export function WoWtoLatLng(
  map: { unproject: (point: [number, number], zoom: number) => unknown },
  mapId: number,
  x: number,
  y: number,
) {
  if (MAPS[mapId].Config.MinX == 63) {
    return
  }

  const pixel = WoWCoordsToPixel(mapId, x, y)
  return map.unproject([pixel.x, pixel.y], MAPS[mapId].Config.MaxZoom)
}
